import { VgApiResult, vgApiRequest, vgPublicRequest } from "./vg-api.service";

/**
 * A provider profile: which kinds of provider an organisation is, and what the public
 * marketplace shows of it. Name and location stay on the Core organisation.
 * Mirrors api/Funely/ProviderProfile.cs.
 */
export type ProviderProfile = {
  orgId: number;
  isFuneralHome: boolean;
  isCemetery: boolean;
  isCrematorium: boolean;
  publicDescription: string;
  isListed: boolean;
};

export type PublicProvider = {
  orgId: number;
  name: string;
  latitude: number | null;
  longitude: number | null;
  isFuneralHome: boolean;
  isCemetery: boolean;
  isCrematorium: boolean;
  publicDescription: string;
};

export type ProviderKind = "funeralHome" | "cemetery" | "crematorium";

/** Same limit as the column and the API's ProviderProfileRules. */
export const MAX_DESCRIPTION_LENGTH = 2000;

type Envelope<T> = { data?: T | null };

export const emptyProfile = (orgId: number): ProviderProfile => ({
  orgId,
  isFuneralHome: false,
  isCemetery: false,
  isCrematorium: false,
  publicDescription: "",
  isListed: false,
});

/** The kinds a provider is, as words, for headings and badges. */
export const providerKindLabels = (p: {
  isFuneralHome: boolean;
  isCemetery: boolean;
  isCrematorium: boolean;
}): string[] => [
  ...(p.isFuneralHome ? ["Funeral home"] : []),
  ...(p.isCemetery ? ["Cemetery"] : []),
  ...(p.isCrematorium ? ["Crematorium"] : []),
];

/** Why a profile cannot be saved, or null. The API checks the same things. */
export const validateProfile = (p: ProviderProfile): string | null => {
  if (!p.orgId || p.orgId <= 0) return "Choose a provider first.";
  if (!p.isFuneralHome && !p.isCemetery && !p.isCrematorium)
    return "Choose at least one kind of provider: funeral home, cemetery or crematorium.";
  if ((p.publicDescription ?? "").length > MAX_DESCRIPTION_LENGTH)
    return `The description is longer than ${MAX_DESCRIPTION_LENGTH} characters.`;
  return null;
};

const unwrap = <T>(result: VgApiResult<Envelope<T>>): VgApiResult<T> =>
  result.error
    ? { data: null, error: result.error }
    : { data: (result.data?.data as T) ?? null, error: null };

export const readProviderProfile = async (
  accessToken: string,
  orgId: number
): Promise<VgApiResult<ProviderProfile>> =>
  unwrap(await vgApiRequest<Envelope<ProviderProfile>>(accessToken, "Read_Provider_Profile", { orgId }));

export const updateProviderProfile = async (
  accessToken: string,
  profile: ProviderProfile
): Promise<VgApiResult<ProviderProfile>> => {
  const invalid = validateProfile(profile);
  if (invalid) return { data: null, error: { message: invalid } };
  return unwrap(
    await vgApiRequest<Envelope<ProviderProfile>>(accessToken, "Update_Provider_Profile", profile)
  );
};

/** A listed provider's public page. No sign-in needed. */
export const readPublicProvider = async (
  orgId: number
): Promise<VgApiResult<PublicProvider>> =>
  unwrap(await vgPublicRequest<Envelope<PublicProvider>>("Read_Public_Provider", { orgId }));

/** Every listed provider, or only one kind. No sign-in needed. */
export const readListedProviders = async (
  kind?: ProviderKind
): Promise<VgApiResult<PublicProvider[]>> => {
  const result = unwrap(
    await vgPublicRequest<Envelope<PublicProvider[]>>("Read_Listed_Providers", kind ? { kind } : {})
  );
  return result.error ? result : { data: result.data ?? [], error: null };
};
